import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import ExploreOffOutlinedIcon from '@mui/icons-material/ExploreOffOutlined';

/**
 * NotFoundPage
 *
 * Catch-all for URLs no route matches (mistyped links, stale bookmarks,
 * notifications rendered before a route rename). Without this the shell
 * rendered header + sidebar around an empty content area, which read as
 * a broken page rather than a wrong address.
 */
const NotFoundPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, width: '100%' }}>
      <Paper
        variant="outlined"
        sx={{
          mt: 6,
          py: 8,
          px: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          borderRadius: 2,
          borderStyle: 'dashed',
          bgcolor: 'transparent',
          maxWidth: 640,
          mx: 'auto',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 72,
            height: 72,
            borderRadius: '50%',
            bgcolor: 'primary.light',
            color: 'primary.main',
            mb: 2,
            '& svg': { fontSize: 34 },
          }}
        >
          <ExploreOffOutlinedIcon />
        </Box>

        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          Page not found
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ maxWidth: 460, mb: 3 }}
        >
          Nothing lives at{' '}
          <Box component="code" sx={{ fontSize: 12.5, wordBreak: 'break-all' }}>
            {location.pathname}
          </Box>
          . The link may be outdated, or the page moved.
        </Typography>

        <Stack direction="row" spacing={1.5}>
          <Button variant="contained" onClick={() => navigate('/')}>
            Go to dashboard
          </Button>
          <Button variant="outlined" onClick={() => navigate(-1)}>
            Go back
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
};

export default NotFoundPage;
